import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { parseQueryString } from '../../../../helpers/path';
import { ROUTE_FORGOT_PASSWORD, ROUTE_FORGOT_PASSWORD_UPDATED } from '../../../../constants/Routes';
import {
	updatePassword,
	checkResetToken,
	deferLogin,
	accountResetPassword,
} from '../../../../actions/authenticationActions';
import ResetLinkInvalid from './ResetLinkInvalid';
import Spinner from '../../../common/spinner/Spinner';
import NewPasswordForm from './NewPasswordForm';
import { initiateTrackStructEvent } from '../../../analytics/Snowplow';
import { EVT_PASSWORD, EVT_RECOVER_PASSWORD_SUBMIT } from '../../../../constants/SnowplowEvents';
import { ONBOARDING_NEW_PASSWORD_CTA } from '../../../../constants/Copy';
import {
	EVT_ELEMENT_INTERACTION,
	EVT_ELEMENT_NAME_PASSWORD_RECOVERY,
	EVT_ELEMENT_TYPE_BUTTON,
	EVT_SET_PASSWORD,
	EVT_ELEMENT_STYLE_PRIMARY,
} from '../../../../constants/SnowplowEvents';
import useSearchParams from '../../../../hooks/useSearchParams';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const NewPasswordContainer = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const location = useLocation();
	const [loading, setLoading] = useState(true);
	const [validLink, setValidLink] = useState(false);
	const [email, setEmail] = useState(null);

	const parsedUrl = parseQueryString(location.search);
	const { getSearchParam } = useSearchParams();
	const userId = getSearchParam('userId');

	useEffect(() => {
		if (!parsedUrl || !parsedUrl.token) {
			setLoading(false);
			return;
		}

		dispatch(checkResetToken(parsedUrl.token, userId))
			.then(({ payload }) => {
				setEmail(payload.email);
				setLoading(false);
				if (payload.isValid) {
					setValidLink(true);
				}
			})
			.catch(() => setLoading(false));
	}, [parsedUrl, userId, dispatch]);

	const handleSubmit = async ({ password }) => {
		const { token, returnUrl } = parsedUrl;

		await initiateTrackStructEvent({
			category: EVT_PASSWORD,
			action: EVT_RECOVER_PASSWORD_SUBMIT,
			label: ONBOARDING_NEW_PASSWORD_CTA,

			rtlElementContext: {
				elementInteraction: EVT_ELEMENT_INTERACTION,
				elementGroup: EVT_ELEMENT_NAME_PASSWORD_RECOVERY,
				elementType: EVT_ELEMENT_TYPE_BUTTON,
				elementName: EVT_SET_PASSWORD,
				elementLabel: ONBOARDING_NEW_PASSWORD_CTA,
				elementStyle: EVT_ELEMENT_STYLE_PRIMARY,
			},
		});

		try {
			await dispatch(updatePassword({ userId, token, password }));
			deferLogin({ password, email, returnUrl });
			history.push(ROUTE_FORGOT_PASSWORD_UPDATED);
		} catch {
			setLoading(false);
			setValidLink(false);
		}
	};

	const resendLink = () => {
		accountResetPassword(parsedUrl.email).catch(() => {
			// Simply forward user to reset password form if anything failed
			history.push(ROUTE_FORGOT_PASSWORD);
		});
	};

	return (
		<>
			{loading && (
				<div className="onboarding__container--loading">
					<Spinner />
				</div>
			)}
			{!loading && !validLink && <ResetLinkInvalid />}
			{!loading && validLink && (
				<NewPasswordForm onSubmit={handleSubmit} email={email} resendLink={resendLink} />
			)}
		</>
	);
};

NewPasswordContainer.propTypes = {
	history: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
	checkResetToken: PropTypes.func.isRequired,
	accountResetPassword: PropTypes.func.isRequired,
	deferLogin: PropTypes.func.isRequired,
	updatePassword: PropTypes.func.isRequired,
};

export default connect(null, { updatePassword, checkResetToken, deferLogin, accountResetPassword })(
	NewPasswordContainer
);
